import React, { useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useStore } from '@nanostores/react';
import { EditorView, basicSetup } from 'codemirror';
import { keymap } from '@codemirror/view';
import { javascript } from '@codemirror/lang-javascript';
import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';
import { json } from '@codemirror/lang-json';
import { Compartment, EditorState, Prec, Transaction } from '@codemirror/state';
import { Logo, cn, Button } from '@ideoz/ui';
import { workbenchStore } from '../stores/workbench';
import { editorStore } from '../stores/editor';
import { extname } from '../utils/path';
import { flushDirtyEditorFilesToWebContainer, resolveFileConflict } from '../runtime/tool-adapters/writeFile';
import { getMergedFileContent, materializeTemplateFileIfNeeded } from '../utils/mergedFiles';
import { strippedUnifiedDiff } from '../utils/workbenchManualDiff';
import { editorSaveKeyBinding } from '../utils/editorSaveKey';

function modKeyLabel(): string {
  if (typeof navigator === 'undefined') return 'Ctrl';
  return /Mac|iPhone|iPod|iPad/i.test(navigator.platform) || navigator.userAgent.includes('Mac') ? '⌘' : 'Ctrl';
}

/** VS Code–light editor surface (no extra theme package). */
const lightEditorChrome = EditorView.theme({
  '&': {
    height: '100%',
    backgroundColor: '#ffffff',
    color: '#24292f',
  },
  '.cm-content': { caretColor: '#24292f' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: '#24292f' },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
    backgroundColor: '#b3d4fc',
  },
  '.cm-activeLine': { backgroundColor: '#f6f8fa' },
  '.cm-gutters': {
    backgroundColor: '#f9fafb',
    color: '#8c959f',
    border: 'none',
    borderRight: '1px solid #e5e7eb',
  },
  '.cm-activeLineGutter': { backgroundColor: '#f6f8fa' },
});

function shortcutRow(keys: string[], label: string) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm w-full max-w-md">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1 shrink-0">
        {keys.map((k, i) => (
          <span key={`${k}-${i}`} className="flex items-center gap-1">
            {i > 0 ? <span className="text-muted-foreground/50 text-xs">+</span> : null}
            <kbd className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-muted border border-border text-foreground shadow-sm">{k}</kbd>
          </span>
        ))}
      </div>
    </div>
  );
}

function getLanguageExtension(path: string) {
  const ext = extname(path);
  switch (ext) {
    case '.js':
    case '.jsx':
    case '.mjs':
      return javascript({ jsx: true });
    case '.ts':
    case '.tsx':
      return javascript({ jsx: true, typescript: true });
    case '.css':
    case '.scss':
      return css();
    case '.html':
      return html();
    case '.json':
      return json();
    default:
      return javascript();
  }
}

/**
 * Editing is paused while Ideoz writes the prototype: an edit made during a build raced the
 * model's writes to the same files and was lost or conflicted (hot edit plan, phase 0).
 */
function editableWhile(paused: boolean) {
  return EditorState.readOnly.of(paused);
}

export function EditorPanel() {
  const mk = modKeyLabel();
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const selectedFile = useStore(editorStore.selectedFile);
  const files = useStore(workbenchStore.files);
  const fileConflicts = useStore(workbenchStore.fileConflicts);
  const webcontainer = useStore(workbenchStore.webcontainer);
  const isStreaming = useStore(workbenchStore.isStreaming);
  const readOnlyCompartmentRef = useRef(new Compartment());
  const lastExternalContentRef = useRef<string>('');
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    if (selectedFile) materializeTemplateFileIfNeeded(selectedFile);
  }, [selectedFile]);

  const fileContent = useMemo(() => {
    if (!selectedFile) return '';
    return files[selectedFile]?.content ?? getMergedFileContent(selectedFile);
  }, [selectedFile, files]);

  useEffect(() => {
    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
      // Skip while a snapshot restore is writing files — a flush here would race the
      // restore's own writes to the same paths (see the debounced handler below).
      if (workbenchStore.restoreInProgress.get()) return;
      const wc = workbenchStore.webcontainer.get();
      if (wc && Object.keys(workbenchStore.getModifiedFiles()).length > 0) {
        void flushDirtyEditorFilesToWebContainer(wc).catch((err) => {
          console.warn('[Workbench] flush on editor close / file switch failed:', err);
        });
      }
    };
  }, [selectedFile]);

  /**
   * Ctrl+S / Cmd+S: sync the pending edit now instead of after the debounce below. The light
   * preview already recompiles from `workbenchStore.files` on every change, so with no
   * WebContainer there is nothing further to do; the binding still consumes the key.
   */
  const flushNow = useCallback(() => {
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }
    if (workbenchStore.isStreaming.get() || workbenchStore.restoreInProgress.get()) return;
    const wc = workbenchStore.webcontainer.get();
    if (!wc || Object.keys(workbenchStore.getModifiedFiles()).length === 0) return;
    void flushDirtyEditorFilesToWebContainer(wc).catch((err) => {
      console.warn('[Workbench] save-key sync to WebContainer failed:', err);
    });
  }, []);

  const handleChange = useCallback(
    (content: string) => {
      if (selectedFile) {
        workbenchStore.files.setKey(selectedFile, { content, modified: true });
        workbenchStore.hasUnbuiltEdits.set(true);
        if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(() => {
          syncTimerRef.current = null;
          if (workbenchStore.isStreaming.get()) return;
          // A snapshot restore (from switching chats or restoring a past generation) is
          // writing files right now — flushing this edit at the same time would race those
          // writes on the same underlying WebContainer filesystem. The restore itself will
          // pick up this edit's content next time `getModifiedFiles()` is read, since the
          // in-memory store write above already happened; only the WebContainer sync waits.
          if (workbenchStore.restoreInProgress.get()) return;
          const wc = workbenchStore.webcontainer.get();
          if (!wc) return;
          void flushDirtyEditorFilesToWebContainer(wc).catch((err) => {
            console.warn('[Workbench] debounced sync to WebContainer failed:', err);
          });
        }, 450);
      }
    },
    [selectedFile],
  );

  const handleChangeRef = useRef(handleChange);
  handleChangeRef.current = handleChange;

  useEffect(() => {
    if (!containerRef.current || !selectedFile) {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
      return;
    }

    if (viewRef.current) {
      viewRef.current.destroy();
      viewRef.current = null;
    }

    const langExt = getLanguageExtension(selectedFile);
    const initial =
      workbenchStore.files.get()[selectedFile]?.content ?? getMergedFileContent(selectedFile);
    lastExternalContentRef.current = initial;

    const state = EditorState.create({
      doc: initial,
      extensions: [
        Prec.highest(keymap.of([editorSaveKeyBinding(flushNow)])),
        basicSetup,
        readOnlyCompartmentRef.current.of(editableWhile(workbenchStore.isStreaming.get())),
        langExt,
        lightEditorChrome,
        EditorView.updateListener.of((update) => {
          if (!update.docChanged) return;
          const newDoc = update.state.doc.toString();
          const fromUser = update.transactions.some((tr) => tr.annotation(Transaction.userEvent));
          if (fromUser) {
            lastExternalContentRef.current = newDoc;
            handleChangeRef.current(newDoc);
          } else {
            lastExternalContentRef.current = newDoc;
          }
        }),
        EditorView.theme({
          '&': { height: '100%' },
          '.cm-scroller': { overflow: 'auto' },
        }),
      ],
    });

    viewRef.current = new EditorView({
      state,
      parent: containerRef.current,
    });

    return () => {
      viewRef.current?.destroy();
      viewRef.current = null;
    };
  }, [selectedFile]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view || !selectedFile) return;

    const userEdited = !!workbenchStore.files.get()[selectedFile]?.modified;
    const hasConflict = !!workbenchStore.fileConflicts.get()[selectedFile];
    if (userEdited || hasConflict) return;

    if (fileContent !== lastExternalContentRef.current) {
      lastExternalContentRef.current = fileContent;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: fileContent },
      });
    }
  }, [fileContent, selectedFile]);

  useEffect(() => {
    viewRef.current?.dispatch({
      effects: readOnlyCompartmentRef.current.reconfigure(editableWhile(isStreaming)),
    });
  }, [isStreaming, selectedFile]);

  const activeConflict = selectedFile ? fileConflicts[selectedFile] : undefined;
  const conflictDiff = useMemo(() => {
    if (!selectedFile || !activeConflict) return '';
    return (
      strippedUnifiedDiff(selectedFile, activeConflict.userContent, activeConflict.aiContent) ??
      `--- your version\n+++ AI version\n(user and AI bodies differ; diff too large to preview)`
    );
  }, [selectedFile, activeConflict]);

  const handleConflictResolution = useCallback(
    (resolution: 'accept' | 'reject') => {
      if (!selectedFile) return;
      const wc = workbenchStore.webcontainer.get();
      if (!wc) return;
      void resolveFileConflict(wc, selectedFile, resolution)
        .then(() => {
          const view = viewRef.current;
          if (!view) return;
          const nextContent = workbenchStore.files.get()[selectedFile]?.content ?? '';
          lastExternalContentRef.current = nextContent;
          view.dispatch({
            changes: { from: 0, to: view.state.doc.length, insert: nextContent },
          });
        })
        .catch((err) => {
          console.warn('[Workbench] conflict resolution failed:', err);
        });
    },
    [selectedFile],
  );

  if (!selectedFile) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6 gap-6 select-none min-h-0">
        <div className="glass-subtle glass-glow rounded-2xl border border-border/50 p-8 max-w-lg w-full space-y-5 shadow-lg backdrop-blur-md">
          <div className="flex justify-center opacity-90">
            <Logo type="logoEn" width={72} height={22} />
          </div>
          <div className="space-y-1">
            <p className="text-foreground text-sm font-medium">No file open</p>
            <p className="text-muted-foreground text-xs">Select a file from the explorer or use quick open.</p>
          </div>
          <div className="space-y-2.5 pt-1 border-t border-border/40">
            {shortcutRow([mk, 'P'], 'Go to file')}
            {shortcutRow([mk, 'S'], 'Sync edits to the preview')}
            {shortcutRow([mk, 'Shift', 'F'], 'Find in files')}
            {shortcutRow([mk, 'Shift', 'P'], 'Command palette')}
            {shortcutRow([mk, '`'], 'Terminal')}
          </div>
        </div>
      </div>
    );
  }

  const pathParts = selectedFile.split('/');

  return (
    <div className="h-full flex flex-col min-h-0 bg-background">
      {activeConflict ? (
        <div className="shrink-0 border-b border-amber-300/80 bg-amber-50 px-3 py-2 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-amber-950 font-medium">
              AI changes conflict with your manual edits
              {activeConflict.aiPath && activeConflict.aiPath !== selectedFile
                ? ` (AI target: ${activeConflict.aiPath})`
                : ''}
            </p>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={!webcontainer}
                onClick={() => handleConflictResolution('reject')}
              >
                Keep mine
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-7 text-xs"
                disabled={!webcontainer}
                onClick={() => handleConflictResolution('accept')}
              >
                Accept AI
              </Button>
            </div>
          </div>
          <pre className="text-[10px] leading-relaxed font-mono text-amber-950/90 bg-white/70 border border-amber-200 rounded-md p-2 max-h-28 overflow-auto whitespace-pre-wrap">
            {conflictDiff}
          </pre>
        </div>
      ) : null}
      <div className="px-3 py-2 glass-subtle border-b border-border/50 shrink-0 min-h-[36px] flex items-center backdrop-blur-sm">
        <nav className="flex items-center gap-0.5 text-xs text-muted-foreground font-mono truncate min-w-0" aria-label="File path">
          {pathParts.map((part, i) => (
            <span key={`${part}-${i}`} className="flex items-center gap-0.5 min-w-0 shrink">
              {i > 0 ? <span className="text-border select-none shrink-0">›</span> : null}
              <span
                className={cn(
                  i === pathParts.length - 1
                    ? 'text-foreground font-medium truncate'
                    : 'text-muted-foreground shrink-0',
                )}
                title={part}
              >
                {part}
              </span>
            </span>
          ))}
        </nav>
      </div>
      {isStreaming ? (
        <div
          className="shrink-0 border-b border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-900"
          role="status"
          data-testid="editor-paused-while-building"
        >
          Editing is paused while Ideoz updates this prototype.
        </div>
      ) : null}
      <div ref={containerRef} className="flex-1 min-h-0 overflow-hidden" />
    </div>
  );
}
